import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useQuery } from '@tanstack/react-query';
import Navbar from './Navbar';
import Footer from './Footer';
import '../styles/TravelDashboard.css';
import { subscribeUser } from '../utils/subscribeUser';

export default function TravelDashboard() {
    const { loggedIn, fetchWithAuth, user } = useAuth();
    const navigate = useNavigate();
    const API_URL = process.env.REACT_APP_API_URL;

    const [notificationsEnabled, setNotificationsEnabled] = useState(false);
    const [checkingSubscription, setCheckingSubscription] = useState(true);
    const [showNotificationModal, setShowNotificationModal] = useState(false);

    // ------------------ React Query fetch function ------------------
    const fetchSavedTrips = async () => {
        const res = await fetchWithAuth(`${API_URL}/get-saved-trips`);
        if (!res.ok) throw new Error('Failed to fetch trips');
        const data = await res.json();
        return data.trips || [];
    };

    // ------------------ React Query hook ------------------
    const { data: savedTrips = [], isLoading, error } = useQuery({
        queryKey: ['savedTrips'],
        queryFn: fetchSavedTrips,
        staleTime: 5 * 60 * 1000,
        cacheTime: 15 * 60 * 1000,
    });

    // Check if user is already subscribed
    useEffect(() => {
        if (!loggedIn) {
            navigate('/login');
            return;
        }

        async function checkSubscription() {
            if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;

            const registration = await navigator.serviceWorker.getRegistration();
            if (!registration) return;

            const subscription = await registration.pushManager.getSubscription();
            setNotificationsEnabled(!!subscription);
            setCheckingSubscription(false);

            // Show modal if not subscribed
            if (!subscription) {
                setShowNotificationModal(true);
            }
        }

        checkSubscription();
    }, [loggedIn, navigate]);

    // Enable notifications on user click
    const handleEnableNotifications = async () => {
        if (!user?.email) {
            alert('Please log in first.');
            return;
        }
        try {
            const permission = await Notification.requestPermission();
            if (permission === 'granted') {
                await subscribeUser(user.email);
                setNotificationsEnabled(true);
                setShowNotificationModal(false);
                alert('Trip notifications enabled successfully!');
            } else {
                alert('Notifications permission denied.');
            }
        } catch (err) {
            console.error('Failed to enable notifications', err);
            alert('Failed to enable notifications. Please try again.');
        }
    };

    // Quick Actions
    const quickActions = [
        { icon: '🗺️', title: 'Explore Places', description: 'Discover amazing destinations', action: () => navigate('/explore-places'), color: 'bg-blue-500' },
        { icon: '✈️', title: 'Plan New Trip', description: 'Create your perfect itinerary', action: () => navigate('/plan-trip'), color: 'bg-green-500' },
        { icon: '🎥', title: 'Travel Vlogs', description: 'Watch exciting travel videos', action: () => navigate('/vlogs'), color: 'bg-red-500' },
        { icon: '👤', title: 'Profile', description: 'View and edit your profile', action: () => navigate('/profile'), color: 'bg-teal-500' },
        { icon: '🧭', title: 'Hire a Guide', description: 'Hire local guides for your trip', action: () => navigate('/book-guide'), color: 'bg-yellow-500' },
        { icon: '🏨', title: 'Find Hotels', description: 'Book comfortable stays', action: () => navigate('/hotel-rooms'), color: 'bg-purple-500' },
        { icon: '🍴', title: 'Discover Food', description: 'Find amazing restaurants', action: () => navigate('/restaurants'), color: 'bg-orange-500' },
        { icon: '🚗', title: 'Transportation', description: 'Find travel options', action: () => navigate('/transportation'), color: 'bg-indigo-500' }

    ];

    if (isLoading) {
        return (
            <div className="dashboard-container">
                <Navbar />
                <div className="loading-container">
                    <div className="loading-spinner"></div>
                    <p>Loading your travel dashboard...</p>
                </div>
                <Footer />
            </div>
        );
    }

    if (error) {
        return (
            <div className="dashboard-container">
                <Navbar />
                <div className="loading-container">
                    <p>Error loading dashboard: {error.message}</p>
                </div>
                <Footer />
            </div>
        );
    }

    return (
        <div className="dashboard-container">
            <Navbar />

            {/* ---------------- Notification Modal ---------------- */}
            {showNotificationModal && (
                <div className="notification-modal">
                    <div className="modal-content">
                        <h3>Enable Trip Notifications 🔔</h3>
                        <p>Stay updated on your saved trips and vlogs!</p>
                        <button onClick={handleEnableNotifications} className="modal-cta-button">
                            Enable Notifications
                        </button>
                        <button onClick={() => setShowNotificationModal(false)} className="modal-close-button">
                            Maybe Later
                        </button>
                    </div>
                </div>
            )}

            <div className="dashboard-content">
                {/* Hero Section */}
                <div className="dashboard-hero">
                    <div className="hero-background">
                        <img
                            src="https://images.unsplash.com/photo-1614088459293-5669fadc3448?crop=entropy&cs=srgb&fm=jpg"
                            alt="Travel Hero"
                            className="hero-image"
                        />
                        <div className="hero-overlay"></div>
                    </div>
                    <div className="hero-content">
                        <h1>Welcome back, {user?.name || 'Traveler'}! 🌟</h1>
                        <p>Ready for your next adventure? Let's plan something amazing together.</p>
                        <button className="cta-button" onClick={() => navigate('/plan-trip')}>Plan New Trip</button>
                    </div>
                </div>

                <div className="dashboard-grid">
                    {/* Quick Actions */}
                    <section className="dashboard-section">
                        <h2>Quick Actions</h2>
                        <div className="quick-actions-grid">
                            {quickActions.map((action, index) => (
                                <div key={index} className="quick-action-card" onClick={action.action}>
                                    <div className={`action-icon ${action.color}`}><span>{action.icon}</span></div>
                                    <h3>{action.title}</h3>
                                    <p>{action.description}</p>
                                </div>
                            ))}
                        </div>
                    </section>

                    {/* Saved Trips */}
                    <section className="dashboard-section">
                        <div className="section-header">
                            <h2>Your Saved Trips</h2>
                            <button className="view-all-btn" onClick={() => navigate('/profile')}>View All</button>
                        </div>
                        <div className="trips-container">
                            {savedTrips.length > 0 ? (
                                savedTrips.slice(0, 3).map((trip, index) => (
                                    <div key={index} className="trip-card">
                                        <div className="trip-header">
                                            <h3>{trip.destination}</h3>
                                            <span className="trip-duration">{trip.days} days</span>
                                        </div>
                                        <p className="trip-interests">{trip.interests?.join(', ')}</p>
                                        <div className="trip-footer">
                                            <span className="trip-date">Saved {new Date(trip.savedAt).toLocaleDateString()}</span>
                                            <button className="view-trip-btn" onClick={() => navigate('/profile')}>View Details</button>
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <div className="no-trips">
                                    <div className="no-trips-icon">✈️</div>
                                    <h3>No trips saved yet</h3>
                                    <p>Start planning your first adventure!</p>
                                    <button className="plan-first-trip-btn" onClick={() => navigate('/plan-trip')}>Plan Your First Trip</button>
                                </div>
                            )}
                        </div>
                    </section>

                    {/* Travel Inspiration */}
                    <section className="dashboard-section inspiration-section">
                        <h2>Travel Inspiration</h2>
                        <div className="inspiration-grid">
                            <div className="inspiration-card">
                                <img src="https://images.unsplash.com/photo-1584467541268-b040f83be3fd?crop=entropy&cs=srgb&fm=jpg" alt="Inspiration" />
                                <div className="inspiration-content">
                                    <h3>Mediterranean Dreams</h3>
                                    <p>Discover the beauty of Greek islands</p>
                                </div>
                            </div>
                            <div className="inspiration-card">
                                <img src="https://images.unsplash.com/photo-1525849306000-cc26ceb5c1d7?crop=entropy&cs=srgb&fm=jpg" alt="Inspiration" />
                                <div className="inspiration-content">
                                    <h3>Tropical Paradise</h3>
                                    <p>Relax on pristine beaches</p>
                                </div>
                            </div>
                        </div>
                    </section>
                </div>
            </div>

            <Footer />
        </div>
    );
}
